import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertTriangle, Files, Mail, Pause, Pencil, Play, Plus, Trash2, Users } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import TableCard from '@/components/layout/TableCard'
import IndexToolbar from '@/components/IndexToolbar'
import { matchesQuery } from '@/lib/matchesQuery'
import ActionMenu from '@/components/ui/ActionMenu'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import { useCreateDripSequence, useDeleteDripSequence, useDripSequences, useUpdateDripSequence } from '@/features/drip/useDrip'
import { cadenceText, windowText } from '@/features/drip/sequenceText'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { cn } from '@/lib/cn'
import type { DripSequence, DripSequenceInput } from '@/api/drip'

const inputOf = (s: DripSequence, overrides: Partial<DripSequenceInput> = {}): DripSequenceInput => ({
  name: s.name,
  enabled: s.enabled,
  steps: s.steps,
  sendWindowStart: s.sendWindowStart ?? null,
  sendWindowEnd: s.sendWindowEnd ?? null,
  ...overrides,
})

/**
 * Sequences: series of emails that go out on a schedule after someone is enrolled. A full-width table —
 * live or paused, the cadence, sending hours, who's in it — each opening on its own page.
 */
export default function Drip() {
  const navigate = useNavigate()
  const { data: sequences, isLoading } = useDripSequences()
  const create = useCreateDripSequence()
  const update = useUpdateDripSequence()
  const del = useDeleteDripSequence()
  const [deleting, setDeleting] = useState<DripSequence | null>(null)
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<'all' | 'live' | 'paused'>('all')
  const all = sequences ?? []
  const shown = all.filter(
    (s) =>
      (status === 'all' || (status === 'live') === s.enabled) &&
      matchesQuery(query, s.name, ...s.steps.map((st) => st.subject)),
  )

  return (
    <PageContainer width="full">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-brand-darkest">Sequences</h1>
          <p className="text-sm text-gray-500">A series of emails sent on a schedule — enrol a list or contacts and each email goes out on its day.</p>
        </div>
        <Link to="/drip/new" className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark">
          <Plus size={15} /> New sequence
        </Link>
      </div>

      {all.length > 0 && (
        <IndexToolbar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search sequences by name or email subject…"
          statuses={[
            { value: 'all', label: 'All', count: all.length },
            { value: 'live', label: 'Live', count: all.filter((s) => s.enabled).length },
            { value: 'paused', label: 'Paused', count: all.filter((s) => !s.enabled).length },
          ]}
          status={status}
          onStatusChange={setStatus}
          shown={shown.length}
          total={all.length}
        />
      )}
      <TableCard>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium text-gray-500 border-b border-gray-100 bg-gray-50/60">
              <th className="px-4 py-2.5">Sequence</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Emails</th>
              <th className="px-3 py-2.5">Sends</th>
              <th className="px-3 py-2.5 text-right">In progress</th>
              <th className="px-3 py-2.5 text-right">Finished</th>
              <th className="px-3 py-2.5 w-28"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <tr key={s.id} onClick={() => navigate(`/drip/${s.id}`)} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/80 cursor-pointer">
                <td className="px-4 py-3 min-w-[12rem]">
                  <Link to={`/drip/${s.id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-brand-darkest hover:text-brand-primary">{s.name}</Link>
                </td>
                <td className="px-3 py-3">
                  <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full', s.enabled ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500')}>
                    <span className={cn('w-1.5 h-1.5 rounded-full', s.enabled ? 'bg-success' : 'bg-gray-400')} />
                    {s.enabled ? 'Live' : 'Paused'}
                  </span>
                </td>
                <td className="px-3 py-3 text-gray-700 whitespace-nowrap">{cadenceText(s.steps)}</td>
                <td className="px-3 py-3 text-gray-700 whitespace-nowrap">{windowText(s)}</td>
                <td className="px-3 py-3 text-right tabular-nums font-medium text-brand-darkest">{s.activeEnrollments}</td>
                <td className="px-3 py-3 text-right tabular-nums text-gray-600">{s.completedEnrollments ?? 0}</td>
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Link to={`/drip/${s.id}`} className="px-2.5 py-1 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50">Edit</Link>
                    <ActionMenu
                      label={`Actions for ${s.name}`}
                      items={[
                        { label: 'Edit emails', icon: <Pencil size={15} />, onClick: () => navigate(`/drip/${s.id}`) },
                        { label: 'Enrol contacts', icon: <Users size={15} />, onClick: () => navigate(`/drip/${s.id}?tab=contacts`) },
                        s.enabled
                          ? { label: 'Pause', icon: <Pause size={15} />, onClick: () => update.mutate({ id: s.id, input: inputOf(s, { enabled: false }) }) }
                          : { label: 'Go live', icon: <Play size={15} />, onClick: () => update.mutate({ id: s.id, input: inputOf(s, { enabled: true }) }) },
                        {
                          label: 'Duplicate',
                          icon: <Files size={15} />,
                          onClick: () => create.mutate(inputOf(s, { name: `${s.name} (copy)`, enabled: false }), { onSuccess: (c) => navigate(`/drip/${c.id}`) }),
                        },
                        { label: 'Delete sequence', icon: <Trash2 size={15} />, danger: true, onClick: () => setDeleting(s) },
                      ]}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {all.length > 0 && shown.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            No sequences match.{' '}
            <button type="button" onClick={() => { setQuery(''); setStatus('all') }} className="font-medium text-brand-primary hover:underline">Clear the search</button>
          </div>
        )}
        {!isLoading && (sequences ?? []).length === 0 && (
          <div className="px-4 py-12 text-center">
            <Mail size={28} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm font-medium text-brand-darkest">No sequences yet</p>
            <p className="text-sm text-gray-500 mb-3">e.g. a welcome email today, a check-in after 3 days and a market update after 2 weeks.</p>
            <Link to="/drip/new" className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark">
              <Plus size={15} /> Create a sequence
            </Link>
          </div>
        )}
      </TableCard>

      {deleting && (
        <Modal
          title="Delete sequence"
          tone="danger"
          icon={<AlertTriangle size={17} />}
          size="sm"
          onClose={() => setDeleting(null)}
          footer={
            <>
              <button type="button" onClick={() => setDeleting(null)} className={modalButton.ghost}>Cancel</button>
              <button type="button" disabled={del.isPending} onClick={() => del.mutate(deleting.id, { onSuccess: () => setDeleting(null) })} className={modalButton.danger}>
                {del.isPending ? 'Deleting…' : 'Delete sequence'}
              </button>
            </>
          }
        >
          <p className="text-sm text-gray-600">
            Delete <span className="font-semibold text-brand-darkest">{deleting.name}</span>?
            {deleting.activeEnrollments > 0
              ? ` ${deleting.activeEnrollments} contact${deleting.activeEnrollments === 1 ? ' is' : 's are'} still in it and won't get the rest of the emails.`
              : ' Nobody is in it right now.'}{' '}
            To stop it for a while instead, pause it.
          </p>
          {del.isError && <p className="text-danger text-xs mt-2">{apiErrorMessage(del.error)}</p>}
        </Modal>
      )}
    </PageContainer>
  )
}
